import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, ChevronDown, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, MoreHorizontal, Calendar, Undo2 } from 'lucide-react';
import { presetRange } from '../components/reports/ReportsControls';
import { toast } from 'sonner';
import { useLeads, LeadsStore } from '../lib/leadsStore';
import { useTeam } from '../lib/teamStore';
import { useAuth } from '../lib/authStore';
import LoanStageBadge, { LOAN_STAGES, normalizeStageKey } from '../components/LoanStageBadge';
import { cn } from '../lib/utils';
import { DatePopover } from '../components/ui/date-popover';

/* ------------------------------------------------------------------
   Fundings — funded loans roll up here for monthly compensation
   reporting. LO pay is based on funded volume per month, so the
   primary axes are (Year, Month) + LO scope (admin only).
   ------------------------------------------------------------------ */

const MONTHS = [
  { v: 0,  short: 'Jan', long: 'January' },
  { v: 1,  short: 'Feb', long: 'February' },
  { v: 2,  short: 'Mar', long: 'March' },
  { v: 3,  short: 'Apr', long: 'April' },
  { v: 4,  short: 'May', long: 'May' },
  { v: 5,  short: 'Jun', long: 'June' },
  { v: 6,  short: 'Jul', long: 'July' },
  { v: 7,  short: 'Aug', long: 'August' },
  { v: 8,  short: 'Sep', long: 'September' },
  { v: 9,  short: 'Oct', long: 'October' },
  { v: 10, short: 'Nov', long: 'November' },
  { v: 11, short: 'Dec', long: 'December' },
];

const SORTS = [
  { key: 'fundedAt',  label: 'Closed Date',  dir: 'desc' },
  { key: 'estAmount', label: 'Loan Amount',  dir: 'desc' },
  { key: 'name',      label: 'Borrower',     dir: 'asc' },
  { key: 'lender',    label: 'Lender',       dir: 'asc' },
];

const money = (n) => {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (!isFinite(v) || v <= 0) return '—';
  return `$${v.toLocaleString('en-US')}`;
};

const lenderFor = (l) => (l?.application?.lender || l?.lender || '').trim();
const loanPurposeLabel = (l) => {
  const raw = l?.application?.loanPurpose || l?.loanIntent || '';
  if (!raw) return '—';
  if (/^[a-z_]+$/.test(raw)) return raw.split('_').map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
  return raw;
};

export default function Fundings() {
  const leads = useLeads();
  const team = useTeam();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // null = whole year
  const [allYear, setAllYear] = useState(false);
  const [sortKey, setSortKey] = useState('fundedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  // Optional custom date-range override. When set, the period filter
  // ignores the month/year picker and pivots on the explicit range so
  // brokers can scope to e.g. "Last 90 days" or a fiscal quarter while
  // still keeping the month chevrons available for normal navigation.
  const [customRange, setCustomRange] = useState(null); // { start, end, preset? } | null

  // ----- All funded leads (regardless of period) ----------------------
  // Withdrawn or denied loans never belong in Fundings even if they were
  // historically funded — they need to disappear the moment they're
  // marked withdrawn/denied on the Lead Profile.
  const allFunded = useMemo(() => {
    return leads.filter((l) =>
      l.loanStatus === 'funded' &&
      l.fundedAt &&
      !l.withdrawn &&
      !l.denied
    );
  }, [leads]);

  // ----- Year/Month axis options (from the data + a sensible recent
  //       window so the dropdowns aren't empty before first funding) --
  const years = useMemo(() => {
    const set = new Set();
    allFunded.forEach((l) => {
      const d = new Date(l.fundedAt);
      if (!isNaN(d)) set.add(d.getFullYear());
    });
    set.add(now.getFullYear());
    return Array.from(set).sort((a, b) => b - a);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFunded]);

  // ----- Period filtering ---------------------------------------------
  const periodFunded = useMemo(() => {
    // Custom date-range mode overrides the month picker entirely.
    if (customRange) {
      const ms = customRange.start.getTime();
      const me = customRange.end.getTime() + 24 * 60 * 60 * 1000 - 1;
      return allFunded.filter((l) => {
        const t = new Date(l.fundedAt).getTime();
        return isFinite(t) && t >= ms && t <= me;
      });
    }
    return allFunded.filter((l) => {
      const d = new Date(l.fundedAt);
      if (isNaN(d)) return false;
      if (d.getFullYear() !== year) return false;
      if (!allYear && d.getMonth() !== month) return false;
      return true;
    });
  }, [allFunded, year, month, allYear, customRange]);

  // Admin LO-scope filter
  const scoped = useMemo(() => {
    if (!isAdmin) {
      // Loan officers only ever see their own fundings.
      return periodFunded.filter((l) => l.assignedTo === user?.id);
    }
    if (assigneeFilter === 'all') return periodFunded;
    if (assigneeFilter === 'unassigned') return periodFunded.filter((l) => !l.assignedTo);
    return periodFunded.filter((l) => l.assignedTo === assigneeFilter);
  }, [periodFunded, isAdmin, assigneeFilter, user?.id]);

  // ----- Sort ----------------------------------------------------------
  const sorted = useMemo(() => {
    const list = [...scoped];
    list.sort((a, b) => {
      let av, bv;
      if (sortKey === 'fundedAt') {
        av = new Date(a.fundedAt).getTime() || 0;
        bv = new Date(b.fundedAt).getTime() || 0;
      } else if (sortKey === 'estAmount') {
        av = Number(a.estAmount) || 0;
        bv = Number(b.estAmount) || 0;
      } else if (sortKey === 'lender') {
        av = lenderFor(a).toLowerCase();
        bv = lenderFor(b).toLowerCase();
      } else {
        av = String(a[sortKey] || '').toLowerCase();
        bv = String(b[sortKey] || '').toLowerCase();
      }
      if (av === bv) return 0;
      const cmp = av > bv ? 1 : -1;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [scoped, sortKey, sortDir]);

  // ----- KPIs ----------------------------------------------------------
  const totals = useMemo(() => {
    const count = sorted.length;
    const volume = sorted.reduce((s, l) => s + (Number(l.estAmount) || 0), 0);
    const avg = count ? volume / count : 0;
    return { count, volume, avg };
  }, [sorted]);

  const stepMonth = (delta) => {
    // Chevron stepping always returns to month mode — clearing any active
    // custom range so the user can resume month-to-month navigation.
    if (customRange) setCustomRange(null);
    if (allYear) {
      setYear((y) => y + delta);
      return;
    }
    setMonth((m) => {
      const next = m + delta;
      if (next < 0) {
        setYear((y) => y - 1);
        return 11;
      }
      if (next > 11) {
        setYear((y) => y + 1);
        return 0;
      }
      return next;
    });
  };

  // Format the custom range header — short month/day, omit year when both
  // bounds fall in the current year to keep the chip tight.
  const fmtRangeShort = (r) => {
    if (!r) return '';
    const sameY = r.start.getFullYear() === r.end.getFullYear();
    const optsS = sameY
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' };
    const optsE = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${r.start.toLocaleDateString('en-US', optsS)} – ${r.end.toLocaleDateString('en-US', optsE)}`;
  };

  const headerLabel = customRange
    ? fmtRangeShort(customRange)
    : (allYear ? `${year}` : `${MONTHS[month].long} ${year}`);

  return (
    <div className="space-y-5 max-w-[1600px] mx-auto" data-testid="fundings-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] text-neutral-500">Workspace / Fundings</div>
          <h1 className="display text-[32px] font-bold tracking-tight text-neutral-900 mt-1">Fundings</h1>
          <p className="text-[13px] text-neutral-500">
            Funded loans for <span className="font-semibold text-neutral-700">{headerLabel}</span>
            {isAdmin && assigneeFilter !== 'all' && (
              <> · {teamLabel(team, assigneeFilter)}</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period picker — month chevrons stay snappy, and the
              picker dropdown now hosts both the month grid AND the
              custom date-range presets so brokers can scope to e.g.
              "Last 90 days" without ever leaving the same control. */}
          <div className="flex items-center rounded-lg border hairline bg-white">
            <button
              data-testid="fundings-period-prev"
              onClick={() => stepMonth(-1)}
              className="h-9 w-8 inline-flex items-center justify-center text-neutral-500 hover:bg-neutral-50 hover:text-neutral-900 transition"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <MonthPicker
              year={year}
              month={month}
              allYear={allYear}
              years={years}
              customRange={customRange}
              fmtRangeShort={fmtRangeShort}
              onChange={(next) => {
                // Any month-picker interaction returns us to month mode.
                if (next.clearRange) {
                  setCustomRange(null);
                  return;
                }
                if (next.setRange) {
                  setCustomRange(next.setRange);
                  return;
                }
                if (customRange) setCustomRange(null);
                if (next.year !== undefined) setYear(next.year);
                if (next.month !== undefined) setMonth(next.month);
                if (next.allYear !== undefined) setAllYear(next.allYear);
              }}
            />
            <button
              data-testid="fundings-period-next"
              onClick={() => stepMonth(1)}
              className="h-9 w-8 inline-flex items-center justify-center text-neutral-500 hover:bg-neutral-50 hover:text-neutral-900 transition"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {isAdmin && <AssigneeFilter value={assigneeFilter} onChange={setAssigneeFilter} team={team} />}
          <SortPicker sortKey={sortKey} sortDir={sortDir} onChange={(k, d) => { setSortKey(k); setSortDir(d); }} />
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiTile label="Funded Loans"  value={totals.count.toString()} testId="fundings-kpi-count" tone="emerald" Icon={Trophy} />
        <KpiTile label="Total Volume"  value={money(totals.volume)}    testId="fundings-kpi-volume" />
        <KpiTile label="Average Loan"  value={money(totals.avg)}       testId="fundings-kpi-avg" />
      </div>

      {/* Table */}
      {sorted.length === 0 ? (
        <EmptyFundings periodLabel={headerLabel} />
      ) : (
        <div className="rounded-2xl border hairline bg-white">
          {/* Custom grid template — explicit column widths give Lender enough
              room to render full names ("Pennymac Wholesale Mortgage", etc.)
              instead of truncating after 14 characters. */}
          <div
            className="grid gap-3 px-5 py-3 text-[10px] uppercase tracking-wider text-neutral-500 font-bold border-b hairline bg-neutral-50/70 rounded-t-2xl"
            style={{ gridTemplateColumns: fundingsGridTemplate(isAdmin) }}
          >
            {isAdmin && <div>Loan Officer</div>}
            <div>Name</div>
            <div>Closing Date</div>
            <div>Funding Date</div>
            <div>Lender</div>
            <div>Purpose</div>
            <div className="text-right">Amount</div>
            <div className="text-center">Actions</div>
          </div>
          <div className="divide-y divide-neutral-100">
            {sorted.map((l) => (
              <FundingRow key={l.id} lead={l} isAdmin={isAdmin} team={team} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function teamLabel(team, id) {
  if (id === 'unassigned') return 'Unassigned';
  const m = (team || []).find((t) => t.id === id);
  return m?.name || 'LO';
}

// Shared grid template for the Fundings table — keeps header + row in
// lockstep. Admin view leads with a Loan Officer column; LO view skips it.
function fundingsGridTemplate(isAdmin) {
  return isAdmin
    ? 'minmax(120px,1fr) minmax(200px,1.8fr) 130px 130px minmax(170px,1.4fr) 110px 110px 60px'
    : 'minmax(200px,2fr) 140px 140px minmax(180px,1.4fr) 120px 120px 60px';
}

function FundingRow({ lead, isAdmin, team }) {
  // Closing date — what the broker originally negotiated; falls back to
  // the auto-stamped fundedAt for legacy/pre-fundingDate deals.
  const closingRaw = lead.estimatedClosingDate || lead.fundedAt;
  const closingLabel = formatDateChip(closingRaw);
  // Funding date — broker-owned, defaults to closing + 3 business days at
  // funding time. Older funded deals (pre-fundingDate field) display "—".
  const fundingLabel = formatDateChip(lead.fundingDate);
  // Admin-only LO label resolved off the cached team list.
  const lo = isAdmin
    ? ((team || []).find((t) => t.id === lead.assignedTo)?.name || 'Unassigned')
    : null;
  const lender = lenderFor(lead);
  return (
    <div
      data-testid={`funding-row-${lead.id}`}
      className="grid gap-3 px-5 py-3.5 items-center hover:bg-neutral-50/60 transition-colors"
      style={{ gridTemplateColumns: fundingsGridTemplate(isAdmin) }}
    >
      {isAdmin && (
        <div className="min-w-0 text-[13px] font-semibold text-neutral-700 truncate" title={lo}>
          {lo === 'Unassigned'
            ? <span className="text-neutral-400 italic">Unassigned</span>
            : lo}
        </div>
      )}
      <div className="min-w-0">
        <Link
          to={`/leads/${lead.id}`}
          className="text-[14px] font-bold text-neutral-900 hover:underline underline-offset-2 truncate block"
        >
          {lead.name}
        </Link>
        <div className="text-[11.5px] text-neutral-500 truncate mt-0.5">
          {lead.source || '—'}{lead.city ? ` · ${lead.city}` : ''}
        </div>
      </div>
      <div className="text-[12.5px] text-neutral-700 tabular-nums font-semibold">{closingLabel}</div>
      <div className="text-[12.5px] text-emerald-700 tabular-nums font-semibold">{fundingLabel}</div>
      <div className="text-[13px] text-neutral-700 truncate" title={lender || '—'}>
        {lender || <span className="text-neutral-400 italic">Not set</span>}
      </div>
      <div className="text-[13px] text-neutral-700 truncate">{loanPurposeLabel(lead)}</div>
      <div className="text-right text-[14px] font-bold text-emerald-700 tabular-nums">
        {money(lead.estAmount)}
      </div>
      <div className="flex justify-center">
        {isAdmin ? <AdminFundingMenu lead={lead} /> : <span className="text-[11px] text-neutral-300">—</span>}
      </div>
    </div>
  );
}

// Render an ISO date or fundedAt timestamp as "Mar 22, 2026" without UTC drift.
function formatDateChip(raw) {
  if (!raw) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (!isNaN(d)) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  const d = new Date(raw);
  return isNaN(d) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function EmptyFundings({ periodLabel }) {
  return (
    <div className="rounded-2xl border hairline bg-white px-8 py-16 text-center" data-testid="fundings-empty">
      <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
        <Trophy className="h-5 w-5 text-emerald-600" />
      </div>
      <h2 className="display text-[20px] font-bold text-neutral-900">No fundings for {periodLabel}</h2>
      <p className="text-[13px] text-neutral-500 mt-1 max-w-md mx-auto">
        Set a deal's stage to <span className="font-semibold text-neutral-700">Funded</span> from the
        {' '}<Link to="/pipeline" className="font-semibold text-neutral-900 underline underline-offset-2">Pipeline</Link>
        {' '}and it will show up here on the funding date.
      </p>
    </div>
  );
}

/* ---------------- Pickers ---------------- */
function KpiTile({ label, value, testId, tone, Icon }) {
  return (
    <div data-testid={testId} className={cn(
      'rounded-2xl border hairline bg-white px-5 py-4 flex items-center gap-3',
      tone === 'emerald' && 'ring-1 ring-emerald-100',
    )}>
      {Icon && (
        <div className={cn(
          'h-9 w-9 rounded-xl flex items-center justify-center shrink-0',
          tone === 'emerald' ? 'bg-emerald-50 text-emerald-700' : 'bg-neutral-100 text-neutral-700',
        )}>
          <Icon className="h-4 w-4" strokeWidth={2.2} />
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">{label}</div>
        <div className="text-[22px] font-bold text-neutral-900 tabular-nums display tracking-tight">{value}</div>
      </div>
    </div>
  );
}

function MonthPicker({ year, month, allYear, years, customRange, fmtRangeShort, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  // Label flips to the custom range when one is active.
  const label = customRange
    ? fmtRangeShort(customRange)
    : (allYear ? String(year) : `${MONTHS[month].long} ${year}`);
  // Custom-range builder state lives inside the popover so the broker
  // can prep a range without committing until they hit Apply.
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const [startStr, setStartStr] = useState(customRange ? customRange.start.toISOString().slice(0, 10) : todayStr);
  const [endStr,   setEndStr]   = useState(customRange ? customRange.end.toISOString().slice(0, 10) : todayStr);
  React.useEffect(() => {
    if (customRange) {
      setStartStr(customRange.start.toISOString().slice(0, 10));
      setEndStr(customRange.end.toISOString().slice(0, 10));
    }
  }, [customRange]);

  const applyPreset = (key) => {
    const r = presetRange(key);
    onChange({ setRange: { start: r.start, end: r.end, preset: key } });
    setOpen(false);
  };
  const applyCustom = () => {
    const s = new Date(startStr);
    const e = new Date(endStr);
    if (isNaN(s) || isNaN(e)) return;
    if (e < s) return;
    onChange({ setRange: { start: s, end: e, preset: null } });
    setOpen(false);
  };
  const clearRange = () => {
    onChange({ clearRange: true });
  };

  const PRESETS = [
    { key: 'month_to_date', label: 'Month to Date' },
    { key: 'last_month',    label: 'Last Month' },
    { key: 'last_7',        label: 'Last 7 days' },
    { key: 'last_30',       label: 'Last 30 days' },
    { key: 'last_90',       label: 'Last 90 days' },
    { key: 'quarter',       label: 'Quarter to Date' },
    { key: 'ytd',           label: 'Year to Date' },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="fundings-period-trigger"
        onClick={() => setOpen((v) => !v)}
        className="h-9 px-3 inline-flex items-center gap-2 text-[12.5px] font-semibold text-neutral-900 hover:bg-neutral-50 transition border-x hairline min-w-[170px] justify-center"
      >
        {label}
        <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
      </button>
      {open && (
        <div
          data-testid="fundings-period-menu"
          className="absolute z-40 left-1/2 -translate-x-1/2 top-full mt-1.5 w-[360px] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden p-3"
        >
          {/* Year row */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-400">Year</span>
            <div className="inline-flex items-center gap-1">
              {years.map((y) => (
                <button
                  key={y}
                  data-testid={`fundings-period-year-${y}`}
                  onClick={() => onChange({ year: y })}
                  className={cn(
                    'h-7 px-2.5 rounded-md text-[12px] font-bold tabular-nums transition',
                    y === year && !customRange
                      ? 'bg-neutral-900 text-white'
                      : 'text-neutral-700 hover:bg-neutral-100',
                  )}
                >
                  {y}
                </button>
              ))}
            </div>
          </div>
          <div className="border-t hairline-soft my-2" />
          {/* All Year toggle */}
          <button
            data-testid="fundings-period-all"
            onClick={() => onChange({ allYear: !allYear })}
            className={cn(
              'w-full text-left px-3 py-2 rounded-md text-[12.5px] font-bold transition mb-1',
              allYear && !customRange
                ? 'bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))]'
                : 'hover:bg-neutral-50 text-neutral-700',
            )}
          >
            {allYear && !customRange ? '✓ ' : ''}All Year ({year})
          </button>
          {/* Month grid */}
          <div className="grid grid-cols-4 gap-1.5 mt-1">
            {MONTHS.map((m) => {
              const active = !allYear && !customRange && m.v === month;
              return (
                <button
                  key={m.v}
                  data-testid={`fundings-period-month-${m.v}`}
                  onClick={() => onChange({ month: m.v, allYear: false })}
                  className={cn(
                    'h-8 rounded-md text-[12px] font-bold transition',
                    active
                      ? 'bg-neutral-900 text-white'
                      : 'text-neutral-700 hover:bg-neutral-100',
                  )}
                >
                  {m.short}
                </button>
              );
            })}
          </div>
          {/* Custom range section */}
          <div className="border-t hairline-soft mt-3 pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-400">Custom Range</span>
              {customRange && (
                <button
                  data-testid="fundings-range-clear"
                  onClick={clearRange}
                  className="text-[10.5px] font-bold text-neutral-500 hover:text-neutral-900 transition"
                >
                  Clear · back to month
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  data-testid={`fundings-range-preset-${p.key}`}
                  onClick={() => applyPreset(p.key)}
                  className={cn(
                    'h-8 rounded-md text-[11.5px] font-semibold transition text-left px-2.5',
                    customRange?.preset === p.key
                      ? 'bg-neutral-900 text-white'
                      : 'text-neutral-700 hover:bg-neutral-100',
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {/* Manual start/end inputs */}
            <div className="grid grid-cols-2 gap-2 mt-1">
              <label className="block">
                <span className="text-[9.5px] uppercase tracking-wider font-bold text-neutral-400">Start</span>
                <input
                  type="date"
                  data-testid="fundings-range-start"
                  value={startStr}
                  onChange={(e) => setStartStr(e.target.value)}
                  className="mt-1 w-full h-8 px-2 text-[12px] rounded-md border hairline tabular-nums"
                />
              </label>
              <label className="block">
                <span className="text-[9.5px] uppercase tracking-wider font-bold text-neutral-400">End</span>
                <input
                  type="date"
                  data-testid="fundings-range-end"
                  value={endStr}
                  onChange={(e) => setEndStr(e.target.value)}
                  className="mt-1 w-full h-8 px-2 text-[12px] rounded-md border hairline tabular-nums"
                />
              </label>
            </div>
            <button
              data-testid="fundings-range-apply"
              onClick={applyCustom}
              className="mt-3 w-full h-9 rounded-md bg-neutral-900 text-white text-[12.5px] font-bold hover:bg-neutral-800 transition"
            >
              Apply custom range
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SortPicker({ sortKey, sortDir, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const active = SORTS.find((s) => s.key === sortKey) || SORTS[0];
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="fundings-sort-trigger"
        onClick={() => setOpen((v) => !v)}
        className="h-9 px-3 rounded-lg border hairline bg-white hover:border-neutral-400 text-[12.5px] font-semibold text-neutral-700 flex items-center gap-2"
      >
        <ArrowUpDown className="h-3.5 w-3.5 text-neutral-400" />
        <span className="text-neutral-900">Sort: {active.label}</span>
        {sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-neutral-500" /> : <ArrowDown className="h-3 w-3 text-neutral-500" />}
        <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
      </button>
      {open && (
        <div
          data-testid="fundings-sort-menu"
          className="absolute right-0 top-full mt-1.5 z-30 w-[220px] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          {SORTS.map((s) => {
            const isActive = s.key === sortKey;
            return (
              <div key={s.key}>
                <button
                  data-testid={`fundings-sort-${s.key}-desc`}
                  onClick={() => { onChange(s.key, 'desc'); setOpen(false); }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-[12.5px] font-semibold transition flex items-center justify-between',
                    isActive && sortDir === 'desc' ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700 hover:bg-neutral-50',
                  )}
                >
                  <span>{s.label}</span>
                  <span className="inline-flex items-center gap-1 text-[10.5px] text-neutral-500">
                    <ArrowDown className="h-3 w-3" /> desc
                  </span>
                </button>
                <button
                  data-testid={`fundings-sort-${s.key}-asc`}
                  onClick={() => { onChange(s.key, 'asc'); setOpen(false); }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-[12.5px] font-semibold transition flex items-center justify-between',
                    isActive && sortDir === 'asc' ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700 hover:bg-neutral-50',
                  )}
                >
                  <span>{s.label}</span>
                  <span className="inline-flex items-center gap-1 text-[10.5px] text-neutral-500">
                    <ArrowUp className="h-3 w-3" /> asc
                  </span>
                </button>
                <div className="border-t hairline-soft" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AssigneeFilter({ value, onChange, team }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const sorted = useMemo(() => {
    const list = (team || []).filter((m) => m.is_active !== false);
    return list.sort((a, b) => {
      if (a.role !== b.role) return a.role === 'lo' ? -1 : 1;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [team]);
  const label =
    value === 'all'        ? 'All LOs' :
    value === 'unassigned' ? 'Unassigned' :
    sorted.find((m) => m.id === value)?.name?.split(' ')[0] || 'LO';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="fundings-assignee-trigger"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'h-9 px-3 rounded-lg border hairline bg-white hover:border-neutral-400 text-[12.5px] font-semibold flex items-center gap-2',
          value !== 'all' ? 'text-neutral-900 border-neutral-900' : 'text-neutral-700',
        )}
      >
        <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-400">LO</span>
        <span>{label}</span>
        <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
      </button>
      {open && (
        <div
          data-testid="fundings-assignee-menu"
          className="absolute right-0 top-full mt-1.5 z-30 w-[220px] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          <button onClick={() => { onChange('all'); setOpen(false); }} className={cn(
            'w-full text-left px-3 py-2 text-[12.5px] font-semibold transition',
            value === 'all' ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700 hover:bg-neutral-50',
          )}>All Loan Officers</button>
          <button onClick={() => { onChange('unassigned'); setOpen(false); }} className={cn(
            'w-full text-left px-3 py-2 text-[12.5px] font-semibold transition',
            value === 'unassigned' ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700 hover:bg-neutral-50',
          )}>Unassigned</button>
          <div className="border-t hairline-soft my-1" />
          {sorted.map((m) => (
            <button
              key={m.id}
              onClick={() => { onChange(m.id); setOpen(false); }}
              className={cn(
                'w-full text-left px-3 py-2 text-[12.5px] font-semibold transition flex items-center justify-between',
                value === m.id ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700 hover:bg-neutral-50',
              )}
            >
              <span className="truncate">{m.name}</span>
              <span className="text-[10.5px] text-neutral-500 shrink-0">{m.role === 'admin' ? 'Admin' : 'Broker'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------
   AdminFundingMenu — admin-only popover on each Fundings row.
   Allows two destructive corrections:
     1. Edit the Close Date (rewrites `fundedAt` to the new ISO so the
        deal moves to the correct month bucket).
     2. Change the loan status (typically used to move a deal back to
        Pipeline by picking any non-funded stage; clearing `fundedAt`
        is handled automatically by the backend patch hook).
   ------------------------------------------------------------------ */
function AdminFundingMenu({ lead }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('root');  // 'root' | 'date' | 'stage'
  const [dateDraft, setDateDraft] = useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setMode('root'); } };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const closeMenu = () => { setOpen(false); setMode('root'); };
  // Seed the draft whenever the "Edit close date" submode opens so the
  // input shows the current value but doesn't write back until the user
  // explicitly clicks Save.
  const openDateMode = () => {
    setDateDraft((lead.estimatedClosingDate || (lead.fundedAt || '').slice(0, 10) || '').slice(0, 10));
    setMode('date');
  };

  const saveDate = async () => {
    const nextYmd = (dateDraft || '').slice(0, 10);
    if (!nextYmd) { toast.error('Pick a date first'); return; }
    closeMenu();
    const iso = `${nextYmd}T00:00:00+00:00`;
    const updated = await LeadsStore.update(lead.id, {
      estimatedClosingDate: nextYmd,
      fundedAt: iso,
    });
    if (updated) toast.success(`${updated.name} → close date moved to ${nextYmd}`);
    else toast.error('Could not update close date');
  };

  const changeStage = async (nextKey) => {
    closeMenu();
    if (normalizeStageKey(lead.loanStatus) === nextKey) return;
    const updated = await LeadsStore.update(lead.id, { loanStatus: nextKey });
    if (updated) {
      toast.success(
        nextKey === 'funded'
          ? `${updated.name} → Funded`
          : `${updated.name} → ${LOAN_STAGES.find((s) => s.key === nextKey)?.label} · moved back to Pipeline`,
      );
    } else {
      toast.error('Could not change stage');
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={`fundings-admin-trigger-${lead.id}`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-neutral-100 text-neutral-500 hover:text-neutral-900 transition"
        title="Admin actions"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div
          data-testid={`fundings-admin-menu-${lead.id}`}
          className="absolute right-0 top-full mt-1.5 z-30 w-[260px] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          {mode === 'root' && (
            <>
              <div className="px-3 py-2 text-[9.5px] uppercase tracking-wider font-bold text-neutral-400 border-b hairline-soft">Admin actions</div>
              <button
                data-testid={`fundings-admin-editdate-${lead.id}`}
                onClick={openDateMode}
                className="w-full text-left px-3 py-2.5 text-[12.5px] font-semibold text-neutral-800 hover:bg-neutral-50 transition flex items-center gap-2"
              >
                <Calendar className="h-3.5 w-3.5 text-neutral-500" /> Edit close date
              </button>
              <button
                data-testid={`fundings-admin-changestage-${lead.id}`}
                onClick={() => setMode('stage')}
                className="w-full text-left px-3 py-2.5 text-[12.5px] font-semibold text-neutral-800 hover:bg-neutral-50 transition flex items-center gap-2"
              >
                <Undo2 className="h-3.5 w-3.5 text-neutral-500" /> Change loan status
              </button>
            </>
          )}
          {mode === 'date' && (
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[9.5px] uppercase tracking-wider font-bold text-neutral-400">Close date</span>
                <button onClick={() => setMode('root')} className="text-[10px] text-neutral-500 hover:text-neutral-900 transition">← back</button>
              </div>
              <DatePopover
                testId={`fundings-admin-date-input-${lead.id}`}
                value={dateDraft}
                onChange={setDateDraft}
                placeholder="mm/dd/yyyy"
              />
              <p className="text-[10.5px] text-neutral-500 mt-2 mb-3">
                Updates the funding month so this deal lands in the correct compensation bucket.
              </p>
              <div className="flex items-center justify-end gap-2">
                <button
                  data-testid={`fundings-admin-date-cancel-${lead.id}`}
                  onClick={closeMenu}
                  className="h-8 px-3 rounded-md border hairline bg-white text-[11.5px] font-bold text-neutral-700 hover:border-neutral-400 transition"
                >Cancel</button>
                <button
                  data-testid={`fundings-admin-date-save-${lead.id}`}
                  onClick={saveDate}
                  disabled={!dateDraft}
                  className={cn(
                    'h-8 px-3 rounded-md text-[11.5px] font-bold transition',
                    dateDraft
                      ? 'btn-ink'
                      : 'bg-neutral-200 text-neutral-400 cursor-not-allowed',
                  )}
                >Save</button>
              </div>
            </div>
          )}
          {mode === 'stage' && (
            <>
              <div className="px-3 py-2 text-[9.5px] uppercase tracking-wider font-bold text-neutral-400 border-b hairline-soft flex items-center justify-between">
                <span>Move to stage</span>
                <button onClick={() => setMode('root')} className="text-[10px] text-neutral-500 hover:text-neutral-900 transition normal-case tracking-normal">← back</button>
              </div>
              {LOAN_STAGES.map((s) => {
                const isCurrent = normalizeStageKey(lead.loanStatus) === s.key;
                return (
                  <button
                    key={s.key}
                    data-testid={`fundings-admin-stage-${lead.id}-${s.key}`}
                    onClick={() => changeStage(s.key)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-left transition',
                      isCurrent ? 'bg-neutral-50' : 'hover:bg-neutral-50',
                    )}
                  >
                    <LoanStageBadge value={s.key} />
                    {s.key !== 'funded' && <span className="ml-auto text-[10px] text-neutral-500">→ Pipeline</span>}
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

